// Terrain d'une carte dessiné une fois par morceaux de 16 × 16 cases, à la taille des images sources : à chaque image,
// le jeu n'a plus qu'à coller les morceaux autour de l'écran, puis à redessiner les seules cases animées (eau, portes…)
// au lieu de toutes les cases de tous les calques.
import type { ElementLayer, MapElement } from '../../shared/map-elements.ts';
import { cellTerrains, drawTerrainArea, drawTerrainCell, onTilesetImagesLoaded, type Scene, type TerrainOpening } from './element-renderer.ts';

const CHUNK = 16;
/**
 * Taille d'une case dans les morceaux : celle des images sources, recopiées telles quelles ; le jeu les agrandit
 * ensuite d'un facteur entier (sa taille de case est un multiple de 16), sans rien changer à l'image.
 */
const CELL = 16;
/** Au-delà (16 Mo, ou le double des morceaux à l'écran), les morceaux vus il y a le plus longtemps sont oubliés. */
const MIN_CHUNKS = 64;

interface Chunk {
  /** Le morceau dessiné, puis, dès qu'elle est prête, son image (gardée par la carte graphique : bien plus rapide à coller). */
  image: HTMLCanvasElement | ImageBitmap;
  /** Cases laissées vides dans le morceau, redessinées à chaque image (voir isLive). */
  live: { x: number; y: number }[];
}

/** Le calque change-t-il d'une image à l'autre (animation, ou lecture au déclenchement, à l'utilisation, au départ) ? */
const isLiveLayer = ({ playback, animation, shape }: ElementLayer) =>
  playback !== 'loop' || (animation?.frames ?? 1) > 1 || (shape.mode === 'single' && shape.sprites.length > 1) || shape.mode === 'directional';

/** Un calque qui sort de sa case chevauche les voisines : l'ordre de dessin compte, les morceaux ne conviennent pas. */
const overflowsCell = ({ layers, gridSize }: MapElement) =>
  layers.some((layer) => layer.offsetX < 0 || layer.offsetY < 0 || layer.offsetX + layer.width > gridSize || layer.offsetY + layer.height > gridSize);

/**
 * Terrain à redessiner à chaque image : animé, ou dans des images d'une autre taille que CELL (réduites ou agrandies
 * d'un facteur non entier, elles ne donneraient pas tout à fait les mêmes pixels une fois dans le morceau).
 */
const live = new WeakMap<MapElement, boolean>();
function isLive(element: MapElement): boolean {
  let value = live.get(element);
  if (value === undefined) live.set(element, value = element.gridSize !== CELL || element.layers.some(isLiveLayer));
  return value;
}

export class TerrainCache {
  private scene: Scene | null = null;
  /** Faux si un terrain de la carte sort de sa case : tout est alors redessiné à chaque image, comme avant. */
  private usable = false;
  private readonly chunks = new Map<number, Chunk>();

  constructor() {
    // Une image arrivée après coup manquait aux morceaux déjà dessinés.
    onTilesetImagesLoaded(() => this.chunks.clear());
  }

  /** Comme drawTerrainArea (tous les calques), pour les cases (x0, y0)–(x1, y1) de la carte. */
  draw(ctx: CanvasRenderingContext2D, scene: Scene, x0: number, y0: number, x1: number, y1: number,
    at: (x: number, y: number) => { px: number; py: number }, tileSize: number, time: number, opened: TerrainOpening): void {
    if (scene !== this.scene) this.prepare(scene);
    if (!this.usable || tileSize % CELL) return drawTerrainArea(ctx, scene, x0, y0, x1, y1, at, tileSize, time, undefined, opened);
    const size = CHUNK * tileSize;
    const cx0 = Math.floor(x0 / CHUNK), cx1 = Math.floor(x1 / CHUNK), cy0 = Math.floor(y0 / CHUNK), cy1 = Math.floor(y1 / CHUNK);
    const keep = Math.max(MIN_CHUNKS, 2 * (cx1 - cx0 + 1) * (cy1 - cy0 + 1));
    ctx.imageSmoothingEnabled = false;
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const chunk = this.chunk(scene, cx, cy, keep);
        const { px, py } = at(cx * CHUNK, cy * CHUNK);
        ctx.drawImage(chunk.image, px, py, size, size);
        for (const { x, y } of chunk.live) {
          if (x < x0 || x > x1 || y < y0 || y > y1) continue;
          const cell = at(x, y);
          drawTerrainCell(ctx, scene, x, y, cell.px, cell.py, tileSize, time, undefined, opened);
        }
      }
    }
  }

  private prepare(scene: Scene): void {
    this.scene = scene;
    this.chunks.clear();
    // Le terrain de la marge compte aussi : sorti de sa case, il déborderait dans la carte.
    const ids = new Set<number>();
    for (const layer of [scene.tiles, ...scene.upperTiles]) for (const id of layer) ids.add(id);
    for (const layers of scene.outerTerrains.values()) for (const terrain of layers) if (terrain) ids.add(terrain.id);
    this.usable = ![...ids].some((id) => { const element = scene.elements.get(id); return element !== undefined && overflowsCell(element); });
  }

  private chunk(scene: Scene, cx: number, cy: number, keep: number): Chunk {
    const key = cx * 65_536 + cy;
    let chunk = this.chunks.get(key);
    if (chunk) {
      // Le plus récemment vu passe en dernier : les premiers sont ceux à oublier.
      this.chunks.delete(key);
    } else {
      chunk = this.paintChunk(scene, cx, cy);
      // Pas de close() sur l'image oubliée : elle a pu être collée dans l'image en cours, pas encore affichée.
      while (this.chunks.size >= keep) this.chunks.delete(this.chunks.keys().next().value!);
    }
    this.chunks.set(key, chunk);
    return chunk;
  }

  private paintChunk(scene: Scene, cx: number, cy: number): Chunk {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = CHUNK * CELL;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    const live: Chunk['live'] = [];
    for (let y = cy * CHUNK; y < Math.min(scene.height, (cy + 1) * CHUNK); y++) {
      for (let x = cx * CHUNK; x < Math.min(scene.width, (cx + 1) * CHUNK); x++) {
        const terrains = cellTerrains(scene, x, y);
        if (terrains.some(({ id }) => { const element = scene.elements.get(id); return element !== undefined && isLive(element); })) live.push({ x, y });
        else if (terrains.length) drawTerrainCell(ctx, scene, x, y, (x - cx * CHUNK) * CELL, (y - cy * CHUNK) * CELL, CELL, 0);
      }
    }
    const chunk: Chunk = { image: canvas, live };
    void createImageBitmap(canvas).then((bitmap) => { chunk.image = bitmap; }, () => {});
    return chunk;
  }
}
